import type { ProjectRender } from "./project-types";
import { clientId } from "./client-id";
import { userApi } from "./api";

type BrowserPptxExportResult = {
  downloadUrl?: string;
  outputManifest?: unknown;
};

type ExportFrameWindow = Window & {
  __dfaiAppExportContext?: { renderId: string; authToken: string };
  __exportDeckPptxToAppBackend?: (options: { renderId: string; authToken: string; idempotencyKey: string }) => Promise<BrowserPptxExportResult>;
  __getEditablePptxExportSnapshot?: () => Record<string, unknown>;
  __getDeckViewModelState?: () => Record<string, unknown>;
  __currentSlideIndex?: number;
};

export type PptxExportMode = "image" | "editable";

export function allowedPptxExportModes(isChinaStandard: boolean): readonly [PptxExportMode, ...PptxExportMode[]] {
  return isChinaStandard ? ["editable"] : ["image", "editable"];
}

export type PptxExportProgress = {
  phase: "preparing" | "queued" | "running" | "downloading";
  message: string;
  percent?: number;
};

export type PptxExportOptions = {
  forceRegenerate?: boolean;
  title?: string;
};

export async function ensureBrowserImagePptx(
  renderId: string,
  iframe?: HTMLIFrameElement | null,
): Promise<BrowserPptxExportResult> {
  const token = userToken();
  if (!token) throw new Error("登录状态已失效，请重新登录后再高级美化。");
  const frameWindow = iframe?.contentWindow as ExportFrameWindow | null | undefined;
  if (!frameWindow?.__exportDeckPptxToAppBackend) {
    throw new Error("标准模式预览尚未准备好，请稍候再开始高级美化。");
  }
  frameWindow.__dfaiAppExportContext = { renderId, authToken: token };
  const result = await frameWindow.__exportDeckPptxToAppBackend({
    renderId,
    authToken: token,
    idempotencyKey: clientId(),
  });
  if (!result?.downloadUrl) throw new Error("标准页面高清图片准备失败，请返回标准模式刷新后重试。");
  return result;
}

export async function exportOrDownloadPptx(
  render: ProjectRender,
  iframe?: HTMLIFrameElement | null,
  mode: PptxExportMode = "image",
  onProgress?: (progress: PptxExportProgress) => void,
  options: PptxExportOptions = {},
): Promise<BrowserPptxExportResult | undefined> {
  const filename = buildPptxDownloadFilename(options.title);
  if (render.renderer !== "dfai") {
    const existingUrl = render.downloadUrl || render.outputManifest?.pptxExport?.url;
    if (existingUrl) {
      await downloadPptxUrl(existingUrl, filename);
      return undefined;
    }
    if (render.renderer === "image2") {
      const result = await exportServerPptx(render.id);
      if (!result?.downloadUrl) throw new Error("PPTX 已生成但服务端没有返回下载地址。");
      await downloadPptxUrl(result.downloadUrl, filename);
      return result;
    }
    throw new Error("当前版本尚未生成 PPTX 文件。");
  }

  if (mode === "image") {
    const existingUrl = render.outputManifest?.pptxExports?.image?.url
      || (!render.outputManifest?.pptxExports?.editable ? render.downloadUrl || render.outputManifest?.pptxExport?.url : undefined);
    if (existingUrl && !options.forceRegenerate) {
      await downloadPptxUrl(existingUrl, filename);
      return undefined;
    }
    const token = userToken();
    if (!token) throw new Error("登录状态已失效，请重新登录后再导出。");
    const frameWindow = iframe?.contentWindow as ExportFrameWindow | null | undefined;
    if (!frameWindow?.__exportDeckPptxToAppBackend) throw new Error("预览页面尚未准备好，请等页面加载完成后再导出。");
    frameWindow.__dfaiAppExportContext = { renderId: render.id, authToken: token };
    const result = await frameWindow.__exportDeckPptxToAppBackend({
      renderId: render.id,
      authToken: token,
      idempotencyKey: clientId(),
    });
    if (!result?.downloadUrl) throw new Error("PPTX 已合成但服务端没有返回下载地址。");
    await downloadPptxUrl(result.downloadUrl, filename);
    return result;
  }

  onProgress?.({ phase: "preparing", message: "正在读取当前页面的编辑状态…", percent: 2 });
  const captured = captureEditablePptxSnapshot(iframe);
  if (!captured.includesLiveEdits) {
    onProgress?.({ phase: "preparing", message: "正在按已保存的页面内容准备可编辑导出…", percent: 4 });
  }
  const result = await exportEditableServerPptx(render.id, captured.snapshot, onProgress);
  if (!result.downloadUrl) throw new Error("可编辑 PPTX 已生成，但服务端没有返回下载地址。");
  onProgress?.({ phase: "downloading", message: "导出完成，正在下载文件…", percent: 100 });
  await downloadPptxUrl(result.downloadUrl, filename);
  return result;
}

export function buildPptxDownloadFilename(title: string | undefined, now = new Date()): string {
  const safeTitle = String(title || "")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
    .replace(/[.\s]+$/g, "")
    .trim()
    .slice(0, 180) || "未命名PPT";
  const date = `${String(now.getFullYear()).padStart(4, "0")}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const time = [now.getHours(), now.getMinutes(), now.getSeconds()]
    .map((part) => String(part).padStart(2, "0"))
    .join("");
  return `${safeTitle}_${date}_${time}.pptx`;
}

async function exportEditableServerPptx(
  renderId: string,
  snapshot: Record<string, unknown>,
  onProgress?: (progress: PptxExportProgress) => void,
): Promise<BrowserPptxExportResult> {
  const queued = await userApi<BrowserPptxExportResult & {
    status?: string;
    task?: EditableExportTaskProgress;
  }>(`/api/app/v2/renders/${encodeURIComponent(renderId)}/export-pptx`, {
    method: "POST",
    body: JSON.stringify({ mode: "editable", idempotencyKey: clientId(), snapshot }),
  });
  if (queued.status === "succeeded" && queued.downloadUrl) return queued;
  emitTaskProgress(queued.status, queued.task, onProgress);

  const deadline = Date.now() + 12 * 60 * 1000;
  const taskQuery = queued.task?.publicId ? `?taskId=${encodeURIComponent(queued.task.publicId)}` : "";
  while (Date.now() < deadline) {
    await wait(1500);
    const status = await userApi<BrowserPptxExportResult & {
      status?: string;
      task?: EditableExportTaskProgress;
    }>(`/api/app/v2/renders/${encodeURIComponent(renderId)}/export-pptx${taskQuery}`, { cache: "no-store" });
    if (status.status === "succeeded" && status.downloadUrl) return status;
    if (status.status === "failed" || status.status === "cancelled") {
      throw new Error(String(status.task?.errorMessage || status.task?.progressText || "可编辑 PPTX 导出未完成"));
    }
    emitTaskProgress(status.status, status.task, onProgress);
  }
  throw new Error("可编辑 PPTX 导出等待超时，请稍后在项目页面重试。");
}

type EditableExportTaskProgress = {
  publicId?: string;
  progressPercent?: number;
  progressText?: unknown;
  errorMessage?: unknown;
};

function emitTaskProgress(
  status: string | undefined,
  task: EditableExportTaskProgress | undefined,
  onProgress?: (progress: PptxExportProgress) => void,
) {
  const running = status === "running";
  const percent = Math.max(5, Math.min(98, Math.floor(Number(task?.progressPercent || (running ? 8 : 5)))));
  onProgress?.({
    phase: running ? "running" : "queued",
    message: String(task?.progressText || (running ? "正在生成可编辑 PPT…" : "任务已提交，正在等待导出服务…")),
    percent,
  });
}

export function captureEditablePptxSnapshot(iframe?: HTMLIFrameElement | null): {
  snapshot: Record<string, unknown>;
  includesLiveEdits: boolean;
} {
  const frameWindow = iframe?.contentWindow as ExportFrameWindow | null | undefined;
  try {
    const snapshot = frameWindow?.__getEditablePptxExportSnapshot?.();
    if (snapshot?.state && typeof snapshot.state === "object" && !Array.isArray(snapshot.state)) {
      return { snapshot, includesLiveEdits: true };
    }
  } catch {
    // Old previews or a tainted canvas can make the richer snapshot unavailable.
  }
  try {
    const state = frameWindow?.__getDeckViewModelState?.();
    if (state && typeof state === "object" && !Array.isArray(state)) {
      return {
        snapshot: {
          themePack: frameWindow?.document.documentElement.dataset.themePack || "",
          currentIndex: frameWindow?.__currentSlideIndex || 0,
          state,
          canvasSnapshots: [],
        },
        includesLiveEdits: true,
      };
    }
  } catch {
    // Cross-origin or legacy preview: the worker can still export the persisted HTML.
  }
  return { snapshot: { state: {}, canvasSnapshots: [] }, includesLiveEdits: false };
}

export async function waitForExportDialogPaint(): Promise<void> {
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve())));
}

function wait(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

export async function downloadPptxUrl(url: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

async function exportServerPptx(renderId: string): Promise<BrowserPptxExportResult> {
  const token = userToken();
  const response = await fetch(`/api/app/v2/renders/${encodeURIComponent(renderId)}/export-pptx`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: "{}",
  });
  const payload = await response.json().catch(() => ({})) as BrowserPptxExportResult & { error?: unknown };
  if (!response.ok) throw new Error(String(payload.error || "PPTX 导出失败"));
  return payload;
}

function userToken() {
  return localStorage.getItem("pptdeep_user_token") || localStorage.getItem("df_ai_ppt_token") || "";
}
